import { PricingType, ProjectStatus, ProjectType, ProjectTypes, VisitWorkareaStatus } from '../../common/constants'
import API from '../api'
import { GpsLog } from '../maps/GpsLog'

export type Timesheet = {
  id: string
  crewMemberId: number
  workareaId?: number
  startedAt: string
  endedAt?: string
  lunch?: number
  gpsLog: GpsLog[]
  canBeEdited: boolean
}

export type Item = {
  catalogItemGuid: string
  estimatedItemGuid?: string
  actualItemGuid?: string
  copyActualItemGuid?: string
  name: string
  units: string
  estimatedQty: number
  visitActualQty: number
  jobActualQty: number
}

export type Visit = {
  id: string
  jobId: number
  visitDate: string
  timesheets: Timesheet[]
  internalNotes?: string
  clientNotes?: string
}

export type Workarea = {
  id: number
  name: string
  scheduledDate: string
  items: Item[]
  status?: ProjectStatus
  isApproved?: boolean
  isOnHold?: boolean
  estimatedOccurrences?: number
  recurringScheduleConfig?: string
  pricingType?: PricingType
}

export type Job = {
  id: number
  name: string
  customerName: string
  workareas: Workarea[]
  type: ProjectType,
  status?: ProjectStatus
}

export type CrewMember = {
  id: number
  name: string
  avatar: string
  isActive: boolean
}

export type SaveVisitTimesheetChanges = {
  changed: Timesheet[]
  added: Timesheet[]
  removedTimesheetIds: string[]
}

export type QuantityChange = {
  catalogItemGuid: string
  quantity: number
  estimatedItemGuid?: string
  actualItemGuid?: string
  copyActualItemGuid?: string
}

export type SaveVisitWorkareaItemChanges = {
  workareaId: number
  items: QuantityChange[]
}

export type WorkareaStatus = {
  workareaId: number
  status?: VisitWorkareaStatus
}

export type JobWorkareaStatus = {
  workareaId: number
  status: ProjectStatus
}

export type SaveVisitPayload = {
  isNewVisit: boolean
  visitId: string | null
  jobId: number
  timesheetChanges: SaveVisitTimesheetChanges
  itemChanges: SaveVisitWorkareaItemChanges[]
  workareaStatuses: WorkareaStatus[]
  jobWorkareaStatuses?: JobWorkareaStatus[]
  internalNotes?: string
  clientNotes?: string
  visitDate?: string
  submitTimesheets: boolean
  sendNotification: boolean
}

export const loadVisitEndpoint = '/web/visits'
export const loadJobEndpoint = '/web/visits/job'
export const loadCrewEndpoint = '/setup/crew'
export const loadItemCategoriesEndpoint = '/items/categories'
export const saveVisitEndpoint = '/web/visits'

export const buildLoadJobEndpoint = (visitId: string, jobId: number) => {
  visitId ||= '00000000-0000-0000-0000-000000000000'
  return `/web/visits/${visitId}/job/${jobId}`
}

export const loadVisit = async (visitId: string): Promise<Visit> => {
  if (!visitId) {
    return { id: '', jobId: 0, visitDate: '', timesheets: [] }
  }
  return (await API.get(`${loadVisitEndpoint}/${visitId}`)).data
}

export const loadJob = async (jobId: number | undefined, visitId: string): Promise<Job> => {
  if (jobId === undefined) {
    return { id: 0, name: '', customerName: '', workareas: [], type: ProjectTypes.Project }
  }
  return (await API.get(buildLoadJobEndpoint(visitId, jobId))).data
}

export const loadCrew = async (): Promise<CrewMember[]> => {
  return (await API.get(loadCrewEndpoint)).data
}

export const saveVisit = async (payload: SaveVisitPayload): Promise<void> => {
  await API.post(saveVisitEndpoint, payload)
}

export const defaultVisitItem = (): Item => ({
  catalogItemGuid: '',
  name: '',
  units: '',
  estimatedQty: 0,
  visitActualQty: 0,
  jobActualQty: 0
})

export type VisitWorkareaStatusRecord = {
  workareaId: number
  status: VisitWorkareaStatus
}

export const loadVisitWorkareaStatuses = async (visitId: string): Promise<VisitWorkareaStatusRecord[]> => {
  return (await API.get(`${loadVisitEndpoint}/${visitId}/workarea-statuses`)).data
}